import type { JSX } from "react";

import { getPetDefinition, usePetSettings } from "@/lib/pet";
import { cn } from "@/lib/utils";

interface PetCompanionProps {
  /** "idle" = empty chat hero, "thinking" = waiting for first token. */
  mode?: "idle" | "thinking";
  /** Visual size of the emoji glyph. */
  size?: "xs" | "sm" | "md" | "lg";
  /** Show a small speech bubble with greeting/thinking copy. */
  showBubble?: boolean;
  /** Override the speech-bubble copy (e.g. agent-specific welcome). */
  message?: string;
  className?: string;
}

const SIZE_TO_TEXT: Record<NonNullable<PetCompanionProps["size"]>, string> = {
  xs: "text-2xl",
  sm: "text-3xl",
  md: "text-5xl",
  lg: "text-7xl",
};

const SIZE_TO_HALO: Record<NonNullable<PetCompanionProps["size"]>, string> = {
  xs: "h-8 w-8",
  sm: "h-10 w-10",
  md: "h-16 w-16",
  lg: "h-24 w-24",
};

/**
 * Tiny mascot rendered in the empty chat state and (optionally)
 * while the agent is composing. Reads the user's pet preference
 * from `localStorage` via {@link usePetSettings}; renders nothing
 * when the user has selected `"off"`.
 *
 * The animations are CSS-only (see `tailwind.config.ts` keyframes
 * `pet-bob`, `pet-think`, `pet-halo`) so this component is cheap
 * even when sitting on top of an active SSE stream.
 */
export function PetCompanion({
  mode = "idle",
  size = "lg",
  showBubble = true,
  message,
  className,
}: PetCompanionProps): JSX.Element | null {
  const { id, name } = usePetSettings();
  const pet = getPetDefinition(id);
  if (!pet) return null;

  const speech = message ?? (mode === "thinking" ? pet.thinking : pet.greeting);
  const animClass = mode === "thinking" ? "animate-pet-think" : "animate-pet-bob";
  const sizeText = SIZE_TO_TEXT[size];
  const haloSize = SIZE_TO_HALO[size];
  const speakerLabel = name.trim() || pet.label;

  return (
    <div
      className={cn("flex flex-col items-center gap-2 select-none", className)}
      aria-hidden="true"
    >
      <div className="relative flex items-center justify-center">
        <span
          className={cn("absolute rounded-full blur-xl animate-pet-halo", haloSize, pet.haloClass)}
        />
        <span className={cn("relative leading-none drop-shadow-sm", sizeText, animClass)}>
          {pet.emoji}
        </span>
      </div>
      {showBubble ? (
        <div
          className={cn(
            "relative max-w-[280px] rounded-2xl bg-card px-3 py-1.5 text-[12px] text-foreground/85 shadow-sm ring-1 ring-border/60",
            "before:absolute before:left-1/2 before:top-0 before:-translate-x-1/2 before:-translate-y-full",
            "before:border-x-[6px] before:border-b-[6px] before:border-x-transparent before:border-b-card",
          )}
        >
          <span className="font-medium text-foreground">{speakerLabel}</span>
          <span className="mx-1 text-muted-foreground">·</span>
          <span>{speech}</span>
        </div>
      ) : null}
    </div>
  );
}
